import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { getFreshAccessToken } from '../../utils/authUtils';
import { api } from '@expat-ui/shared';
import { Country, Currency } from '../../types/api.types';
import { useOnboardingChecklist } from '../../hooks/useApi';
import styles from './OnboardingPage.module.scss';

type Step = 'household' | 'profile' | 'timeline' | 'checklist';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

const CURRENCIES: { value: Currency; label: string }[] = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'CAD', label: 'CAD (C$)' },
];

const STEP_COUNT = 4;

interface ResidencyEntry {
    startAge: number;
    endAge: number;
    country: Country;
}

export function OnboardingPage() {
    const navigate = useNavigate();
    const { user, markHouseholdCreated } = useAuth();
    const [step, setStep] = useState<Step>('household');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [birthYear, setBirthYear] = useState(String(new Date().getFullYear() - 35));
    const [targetRetirementAge, setTargetRetirementAge] = useState('55');
    const [baseCurrency, setBaseCurrency] = useState<Currency | ''>('');
    const [currentCountry, setCurrentCountry] = useState<Country | ''>('');
    const [householdName, setHouseholdName] = useState(`${user?.firstName || 'My'}'s Household`);
    const [residencyEntries, setResidencyEntries] = useState<ResidencyEntry[]>([]);

    const { checklist, loading: checklistLoading, markTimelineComplete } = useOnboardingChecklist();

    const currentAge = new Date().getFullYear() - Number(birthYear);
    const retirementAge = Number(targetRetirementAge);

    const stepIndex: Record<Step, number> = { household: 1, profile: 2, timeline: 3, checklist: 4 };

    const handleHouseholdSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            await api.post('/household', { name: householdName }, token);
            markHouseholdCreated();
            setStep('profile');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create household');
        } finally {
            setSaving(false);
        }
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');

            await api.put(
                '/profile',
                {
                    birthYear: Number(birthYear),
                    targetRetirementAge: retirementAge,
                    baseCurrency: baseCurrency as Currency,
                    displayName: user?.firstName || user?.email || 'User',
                    residencyHistory: [
                        {
                            startAge: currentAge,
                            endAge: retirementAge,
                            country: currentCountry as Country,
                        },
                    ],
                },
                token,
            );

            setResidencyEntries([{ startAge: currentAge, endAge: retirementAge, country: currentCountry as Country }]);
            setStep('timeline');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const addResidencyEntry = () => {
        const lastEntry = residencyEntries[residencyEntries.length - 1];
        const newStart = lastEntry ? lastEntry.endAge : currentAge;
        setResidencyEntries([...residencyEntries, { startAge: newStart, endAge: newStart + 20, country: 'INDIA' }]);
    };

    const removeResidencyEntry = (index: number) => {
        setResidencyEntries(residencyEntries.filter((_, i) => i !== index));
    };

    const updateResidencyEntry = (index: number, field: keyof ResidencyEntry, value: string | number) => {
        setResidencyEntries(
            residencyEntries.map((entry, i) =>
                i === index ? { ...entry, [field]: field === 'country' ? value : Number(value) } : entry,
            ),
        );
    };

    const handleTimelineSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');

            await api.put('/profile', { residencyHistory: residencyEntries }, token);
            await markTimelineComplete();
            setStep('checklist');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save timeline');
        } finally {
            setSaving(false);
        }
    };

    const handleFinish = () => {
        navigate('/dashboard', { replace: true });
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.progress}>
                    {Array.from({ length: STEP_COUNT }, (_, i) => {
                        const stepNum = i + 1;
                        const current = stepIndex[step];
                        return (
                            <div key={i} className={styles.progressStep}>
                                <div
                                    className={`${styles.dot} ${stepNum < current ? styles.completed : stepNum === current ? styles.active : ''}`}
                                />
                                {i < STEP_COUNT - 1 && (
                                    <div
                                        className={`${styles.line} ${stepNum < current ? styles.lineCompleted : ''}`}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                {step === 'household' && (
                    <form onSubmit={handleHouseholdSubmit}>
                        <h2 className={styles.heading}>Create Your Household</h2>
                        <p className={styles.subheading}>
                            A household groups your family&apos;s cross-border finances together.
                        </p>

                        <div className={styles.field}>
                            <label className={styles.label}>Household Name</label>
                            <input
                                type="text"
                                className={styles.input}
                                value={householdName}
                                onChange={(e) => setHouseholdName(e.target.value)}
                                required
                            />
                        </div>

                        {error && <p className={styles.error}>{error}</p>}

                        <button type="submit" className={styles.button} disabled={saving}>
                            {saving ? 'Creating...' : 'Continue'}
                        </button>
                    </form>
                )}

                {step === 'profile' && (
                    <form onSubmit={handleProfileSubmit}>
                        <h2 className={styles.heading}>Set Up Your Profile</h2>
                        <p className={styles.subheading}>Tell us about your financial planning horizon.</p>

                        <div className={styles.field}>
                            <label className={styles.label}>Year of Birth</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={birthYear}
                                onChange={(e) => setBirthYear(e.target.value)}
                                min={1940}
                                max={new Date().getFullYear() - 18}
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Target Retirement Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={targetRetirementAge}
                                onChange={(e) => setTargetRetirementAge(e.target.value)}
                                min={currentAge + 1}
                                max={90}
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Current Country of Residence</label>
                            <select
                                className={styles.input}
                                value={currentCountry}
                                onChange={(e) => setCurrentCountry(e.target.value as Country)}
                                required
                            >
                                <option value="" disabled>
                                    Select country
                                </option>
                                {COUNTRIES.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Base Display Currency</label>
                            <select
                                className={styles.input}
                                value={baseCurrency}
                                onChange={(e) => setBaseCurrency(e.target.value as Currency)}
                                required
                            >
                                <option value="" disabled>
                                    Select currency
                                </option>
                                {CURRENCIES.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {error && <p className={styles.error}>{error}</p>}

                        <button type="submit" className={styles.button} disabled={saving}>
                            {saving ? 'Saving...' : 'Continue'}
                        </button>
                    </form>
                )}

                {step === 'timeline' && (
                    <form onSubmit={handleTimelineSubmit}>
                        <h2 className={styles.heading}>Your Life Timeline</h2>
                        <p className={styles.subheading}>
                            Tell us where you&apos;ve lived and where you plan to retire. We use this to know which
                            accounts and expenses matter to you.
                        </p>

                        <div className={styles.timelineList}>
                            {residencyEntries.map((entry, index) => (
                                <div key={index} className={styles.timelineEntry}>
                                    <div className={styles.timelineEntryHeader}>
                                        <span className={styles.timelineEntryLabel}>
                                            {index === 0 ? 'Currently in' : 'Also lived in'}
                                        </span>
                                        {index > 0 && (
                                            <button
                                                type="button"
                                                className={styles.removeButton}
                                                onClick={() => removeResidencyEntry(index)}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className={styles.timelineEntryFields}>
                                        <select
                                            className={styles.input}
                                            value={entry.country}
                                            onChange={(e) => updateResidencyEntry(index, 'country', e.target.value)}
                                        >
                                            {COUNTRIES.map((c) => (
                                                <option key={c.value} value={c.value}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={styles.ageRange}>
                                            <div className={styles.ageField}>
                                                <label className={styles.ageLabel}>From age</label>
                                                <input
                                                    type="number"
                                                    className={styles.ageInput}
                                                    value={entry.startAge}
                                                    min={0}
                                                    max={89}
                                                    onChange={(e) =>
                                                        updateResidencyEntry(index, 'startAge', e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div className={styles.ageField}>
                                                <label className={styles.ageLabel}>To age</label>
                                                <input
                                                    type="number"
                                                    className={styles.ageInput}
                                                    value={entry.endAge}
                                                    min={entry.startAge + 1}
                                                    max={100}
                                                    onChange={(e) =>
                                                        updateResidencyEntry(index, 'endAge', e.target.value)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button type="button" className={styles.addButton} onClick={addResidencyEntry}>
                            + Add another country
                        </button>

                        <p className={styles.timelineHint}>
                            Tip: add your retirement country with a start age of {retirementAge} so we know where
                            you&apos;ll retire.
                        </p>

                        {error && <p className={styles.error}>{error}</p>}

                        <button type="submit" className={styles.button} disabled={saving}>
                            {saving ? 'Saving...' : 'Continue'}
                        </button>
                    </form>
                )}

                {step === 'checklist' && (
                    <div>
                        <h2 className={styles.heading}>Your Setup Checklist</h2>
                        <p className={styles.subheading}>
                            Based on your profile, here&apos;s what you&apos;ll want to fill in. No rush — complete
                            these at your own pace from the dashboard.
                        </p>

                        {checklistLoading ? (
                            <p className={styles.checklistLoading}>Building your checklist...</p>
                        ) : (
                            <div className={styles.checklistPreview}>
                                {(checklist?.sections ?? []).map((section) => (
                                    <div key={section.id} className={styles.checklistItem}>
                                        <div className={styles.checklistIcon}>
                                            {section.status === 'complete' ? '✓' : '○'}
                                        </div>
                                        <div>
                                            <div className={styles.checklistItemLabel}>{section.label}</div>
                                            <div className={styles.checklistItemDesc}>{section.description}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className={styles.checklistFooter}>
                            You&apos;ll see a progress tracker on your dashboard until everything is set up.
                        </p>

                        <button className={styles.button} onClick={handleFinish}>
                            Go to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
